import { SubscriptionCodes } from '../helpers/constants';
import { deleteIn, getIn, setIn } from '../helpers/helpers';
import { canIUse, clientHasPermissionToTier, Features } from '../helpers/PermissionUtility';
import { deepClone } from '../helpers/utility';
import { getUsedKeyInConfig } from './StepMetadata';
import {
  Action,
  AppItemPermissionLevel,
  ClientAppPermissions,
  InputConfigItem,
  PublishedApp,
  Recipe,
  Trigger,
  Workflow,
  WorkflowEditorState,
  WorkflowStep,
  WorkflowStepConfig,
  WorkflowTrigger
} from './workflow-types';

export const getLastStepKeyPath = (state: WorkflowEditorState) => {
  const keyPath: (string | number)[] = ['workflow', 'steps'];
  if (state.workflow.steps != null) {
    keyPath.push(state.workflow.steps.length - 1);
  }
  return keyPath;
};

export const getStepKeyPathById = (state: WorkflowEditorState, stepId: string) => {
  const keyPath: (string | number)[] = ['workflow', 'steps'];
  if (state.workflow.steps !== null) {
    const index = GetStepIndexById(state, stepId);
    if (index !== -1) {
      keyPath.push(index);
    }
  }
  return keyPath;
};

export const GetStepIndexById = (state: WorkflowEditorState, stepId: string) => {
  return state.workflow.steps ? state.workflow.steps.findIndex((x) => x.id === stepId) : -1;
};

export const getStepIds = (state: WorkflowEditorState) => {
  return state.workflow.steps.map((x) => x.id);
};

export const getSelectedStepKeyPath = (state: WorkflowEditorState) => {
  const selectedStepId = state.selectedStepId;
  const selectedTrigger = state.selectedTrigger;
  let keyPath = null;
  if (selectedStepId) {
    keyPath = getStepKeyPathById(state, selectedStepId);
  } else if (selectedTrigger) {
    keyPath = ['workflow', 'trigger'];
  }
  return keyPath;
};

export const getPropertyFullKeyPath = (state: WorkflowEditorState, propKeyPath: string[]) => {
  const selectedStepKeyPath = getSelectedStepKeyPath(state);
  const keyPath = [...selectedStepKeyPath, ...propKeyPath];
  return keyPath;
};

export const addStep = (state: WorkflowEditorState, newStep: WorkflowStep, stepPosition?: number) => {
  const newSteps = state.workflow.steps || [];

  if (!stepPosition) {
    newSteps.push(newStep);
  } else {
    newSteps.splice(stepPosition - 1, 0, newStep);
  }

  return setIn(state, ['workflow', 'steps'], newSteps);
};

export const cloneStep = (state: WorkflowEditorState, stepId: string) => {
  const step = getIn<WorkflowStep>(state, getStepKeyPathById(state, stepId));
  const stepIds = getStepIds(state);

  let newStepId = step.id;
  const stepIdNumberSuffix = newStepId.match(/\d+$/);
  const stepIdPrefix = newStepId.replace(/\d+$/, '');
  let stepIdSuffix = stepIdNumberSuffix === null ? 0 : parseInt(stepIdNumberSuffix[0], 10);
  do {
    stepIdSuffix += 1;
    newStepId = stepIdPrefix + stepIdSuffix;
  } while (stepIds.includes(newStepId));

  const newStep = deepClone(step);
  newStep.id = newStepId;
  addStep(state, newStep);
  state.selectedStepId = newStepId;
};

export const deleteStep = (state: WorkflowEditorState, stepId: string) => {
  const newSteps = state.workflow.steps;
  newSteps.splice(GetStepIndexById(state, stepId), 1);
  setIn(state, ['workflow', 'steps'], newSteps);

  const newMetadataSteps = state.metadata.steps;
  const metadataIndex = newMetadataSteps.findIndex((x) => x.id === stepId);
  if (metadataIndex !== -1) {
    newMetadataSteps.splice(metadataIndex, 1);
    setIn(state, ['metadata', 'steps'], newMetadataSteps);
  }

  if (state.selectedStepId === stepId) {
    state.selectedStepId = '';
  }
};

export const moveStepDown = (state: WorkflowEditorState, stepId: string) => {
  const stepIndex = GetStepIndexById(state, stepId);
  if (stepIndex === -1 || stepIndex === state.workflow.steps.length - 1) {
    return state;
  }
  const step = state.workflow.steps[stepIndex];
  const newSteps = state.workflow.steps;
  newSteps[stepIndex] = newSteps[stepIndex + 1];
  newSteps[stepIndex + 1] = step;
  setIn(state, ['workflow', 'steps'], newSteps);
};

export const moveStepUp = (state: WorkflowEditorState, stepId: string) => {
  const stepIndex = GetStepIndexById(state, stepId);
  if (stepIndex === -1 || stepIndex === 0) {
    return state;
  }
  const step = state.workflow.steps[stepIndex];
  const newSteps = state.workflow.steps;
  newSteps[stepIndex] = newSteps[stepIndex - 1];
  newSteps[stepIndex - 1] = step;
  setIn(state, ['workflow', 'steps'], newSteps);
};

export const moveStep = (state: WorkflowEditorState, stepId: string, stepPosition: number) => {
  const stepIndex = GetStepIndexById(state, stepId);
  const insertIndex = stepPosition - 1;
  if (stepIndex === -1 || stepIndex === insertIndex) {
    return state;
  }

  const step = state.workflow.steps[stepIndex];
  const newSteps = state.workflow.steps;
  newSteps.splice(stepIndex, 1);
  newSteps.splice(insertIndex, 0, step);
  setIn(state, ['workflow', 'steps'], newSteps);
};

export const updateStepConfigName = (state: WorkflowEditorState, payload: TypeMePlease) => {
  const keyPath = getPropertyFullKeyPath(state, payload.propKeyPath);
  const stepConfig = getIn<WorkflowStepConfig>(state, keyPath);
  const newConfig = {};
  for (const key of Object.keys(stepConfig)) {
    const value = stepConfig[key];
    deleteIn(state, [...keyPath, ...[key]]);
    if (key === payload.oldName) {
      newConfig[payload.newName] = value;
    } else {
      newConfig[key] = value;
    }
  }
  setIn(state, keyPath, newConfig);
};

export const removeStepProperty = (state: WorkflowEditorState, payload: TypeMePlease) => {
  const keyPath = [...getPropertyFullKeyPath(state, payload.propKeyPath), payload.name];
  deleteIn(state, keyPath);
};

export const updateStepId = (state: WorkflowEditorState, payload: TypeMePlease) => {
  const keyPath = getStepKeyPathById(state, payload.currentStepId);

  // Need to update the id in the metadata step
  const metadataStepIndex = state.metadata?.steps?.findIndex((x) => x.id === payload.currentStepId) || -1;
  if (metadataStepIndex !== -1) {
    state.metadata.steps[metadataStepIndex] = { ...state.metadata.steps[metadataStepIndex], id: payload.newStepId };
  }
  // Update the id on the actual step
  setIn(state, [...keyPath, 'id'], payload.newStepId);

  if (state.selectedStepId === payload.currentStepId) {
    state.selectedStepId = payload.newStepId;
  }

  for (const step of state.workflow.steps) {
    for (const key of Object.keys(step.config)) {
      if (step.config[key]) {
        if (step.config[key].startsWith('=')) {
          step.config[key] = step.config[key].replace(new RegExp(`\\[${payload.currentStepId}\\.`, 'g'), `[${payload.newStepId}.`);
        } else {
          step.config[key] = step.config[key].replace(new RegExp(`#{${payload.currentStepId}\\.`, 'g'), `#{${payload.newStepId}.`);
        }
      }
    }
  }
};

export const updateStep = (state: WorkflowEditorState, payload: TypeMePlease) => {
  const keyPath = getPropertyFullKeyPath(state, payload.propKeyPath);
  setIn(state, keyPath, payload.propValue);
};

export const getNextStepId = (state: WorkflowEditorState, stepId: string) => {
  const nextStepIndex = GetStepIndexById(state, stepId) + 1;
  if (state.workflow.steps && nextStepIndex < state.workflow.steps.length) {
    return state.workflow.steps[nextStepIndex].id;
  }

  return null;
};

export const addStepProperty = (state: WorkflowEditorState, propKeyPath: string[], value: string) => {
  const keyPath = getPropertyFullKeyPath(state, propKeyPath);
  const partialKeyPath = [];
  for (const keyPart of keyPath) {
    partialKeyPath.push(keyPart);
    if (!getIn(state, partialKeyPath) && partialKeyPath.length < keyPath.length) {
      setIn(state, partialKeyPath, {});
    }
  }
  setIn(state, keyPath, value);
};

export function metadataUpdateInputConfig(state: WorkflowEditorState, payload: TypeMePlease) {
  const { stepId, key, configName, configValue } = payload;

  if (!state.metadata) {
    state.metadata = {};
  }
  let steps = state.metadata.steps;
  if (!steps) {
    steps = [];
    state.metadata.steps = steps;
  }
  let stepIndex = steps.findIndex((x) => x.id === stepId);
  if (stepIndex === -1) {
    stepIndex = steps.length;
    state.metadata.steps[stepIndex] = { id: stepId };
  }
  if (!state.metadata.steps[stepIndex].inputConfig) {
    state.metadata.steps[stepIndex].inputConfig = {};
  }
  if (!state.metadata.steps[stepIndex].inputConfig[key]) {
    state.metadata.steps[stepIndex].inputConfig[key] = {};
  }

  state.metadata.steps[stepIndex].inputConfig[key][configName] = configValue;
}

export function filterEmptyObjects(array) {
  return array.filter((item) => Object.keys(item).length !== 0);
}

export function metadataUpdateStepMetadata(state: WorkflowEditorState, payload: TypeMePlease) {
  const { stepId, dynamicMetadata } = payload;

  if (!state.metadata) {
    state.metadata = {};
  }
  let steps = state.metadata.steps;
  if (!steps) {
    steps = [];
    state.metadata.steps = steps;
  }
  let stepIndex = steps.findIndex((x) => x.id === stepId);
  if (stepIndex === -1) {
    stepIndex = steps.length;
    state.metadata.steps[stepIndex] = { id: stepId };
  }

  if (state?.metadata?.steps[stepIndex].dynamicMetadata) {
    const filteredDynamicMetadata = {
      ...dynamicMetadata,
      outputs: filterEmptyObjects(dynamicMetadata.outputs)
    };

    const filteredStateDynamicMetadata = {
      ...state.metadata.steps[stepIndex].dynamicMetadata,
      outputs: filterEmptyObjects(state.metadata.steps[stepIndex].dynamicMetadata.outputs)
    };

    const areEqual = JSON.stringify(filteredDynamicMetadata) === JSON.stringify(filteredStateDynamicMetadata);

    if (areEqual) {
      return;
    }
  }

  state.metadata.steps[stepIndex].dynamicMetadata = dynamicMetadata;

  if (!dynamicMetadata) {
    return;
  }

  state.isDirty = true;
}

export function removeBlankObjectsFromWorkflowStepMetadata(state: WorkflowEditorState, payload: TypeMePlease) {
  const getStepMetadataById = (id: string) => {
    return payload.stepsMetadata?.find((x) => x.id === id);
  };

  (state.workflow?.steps || []).some((step) => {
    const metadataStep = getStepMetadataById(step.id);
    const adapterName = step.action.adapterName;
    const config = step.config;
    metadataStep.metadata.inputs.some((input) => {
      //get the input value
      const key = getUsedKeyInConfig(config, input, adapterName);
      const value = step.config[key];

      // Check if value is an objectList, not required, and contains empty objects to be removed
      if (!input.required && input.type === 'objectList' && value !== undefined && value) {
        const parsedValue = JSON.parse(value);
        if (Array.isArray(parsedValue) && parsedValue.length > 1) {
          const emptyObject = parsedValue.find((item) => Object.keys(item).length === 0);
          if (emptyObject) {
            step.config[key] = JSON.stringify(parsedValue.filter((item) => Object.keys(item).length !== 0));
          }
        }
      }
    });
  });
  metadataUpdateStepMetadata(state, payload);
}

export function generateWorkflowFromRecipe(recipe: Recipe, templateId = null): Workflow {
  const steps = recipe.steps.map((x) => {
    const step: WorkflowStep = {
      id: x.id,
      action: {
        adapterName: 'apptask'
      },
      config: {
        appId: x.appId,
        appCommand: x.appCommand
      }
    };

    for (const key in x.config) {
      const value = x.config[key];

      step.config[`appinput_${key}`] = value;
    }

    return step;
  });

  const workflow: Workflow = {
    name: recipe.name,
    active: true,
    recipeId: recipe.id,
    steps: steps,
    trigger: {
      ...recipe.trigger,
      entityId: templateId ?? null
    }
  };

  return workflow;
}

export function getFriendlyPermissionLevelName(permissionLevel: AppItemPermissionLevel): string {
  switch (permissionLevel) {
    case AppItemPermissionLevel.Team:
      return 'Team';
    case AppItemPermissionLevel.Advanced:
      return 'Advanced';
    case AppItemPermissionLevel.Enterprise:
      return 'Enterprise';
    case AppItemPermissionLevel.AddOn:
      return 'Add On';
    case AppItemPermissionLevel.Unlisted:
      return 'Unlisted';
    case AppItemPermissionLevel.Deprecated:
      return 'Deprecated';
  }
}

export function getSubscriptionCodePriority(subscriptionCode: string): number {
  switch (subscriptionCode) {
    case SubscriptionCodes.Basic:
      return 0;
    case SubscriptionCodes.Team:
      return 1;
    case SubscriptionCodes.Advanced:
      return 2;
    case SubscriptionCodes.Enterprise:
      return 3;
  }
}

export function hasCommandTierPermission(commandTier: string, subscriptionCode: string): boolean {
  switch (commandTier) {
    case AppItemPermissionLevel.Team:
      return getSubscriptionCodePriority(subscriptionCode) >= 1;
    case AppItemPermissionLevel.Advanced:
      return getSubscriptionCodePriority(subscriptionCode) >= 2;
    case AppItemPermissionLevel.Enterprise:
      return getSubscriptionCodePriority(subscriptionCode) >= 3;
  }
}

export function getEditionCodeFromPermissionLevel(permissionLevel: AppItemPermissionLevel): string {
  switch (getFriendlyPermissionLevelName(permissionLevel)) {
    case 'Team':
      return SubscriptionCodes.Team;
    case 'Advanced':
      return SubscriptionCodes.Advanced;
    case 'Enterprise':
      return SubscriptionCodes.Enterprise;
    default:
      return null;
  }
}

export function isCommandInTrialRecipes(appId, commandName, allTrialRecipes: Recipe[]) {
  if (!allTrialRecipes || appId == null) {
    return false;
  }

  //Ensure that all passed recipes are trial recipes
  const areAllTrialRecipes = allTrialRecipes.every((x) => x.trial);
  if (!areAllTrialRecipes) {
    throw new Error('Non-trial recipes passed to isCommandInTrialRecipes');
  }

  const isTrialRecipeCommand = allTrialRecipes.some((r) => r.steps.some((s) => s.appId === appId && s.appCommand === commandName));

  return isTrialRecipeCommand;
}

export function hasCommandPermission(
  commandName: string,
  commandPermissionLevel: string,
  clientAppPermissions: ClientAppPermissions,
  subscriptionCode: string,
  appId,
  trialRecipes: Recipe[]
): boolean {
  const trialPermissionLevel =
    subscriptionCode === SubscriptionCodes.Trial && appId && trialRecipes ? isCommandInTrialRecipes(appId, commandName, trialRecipes) : false;
  //if the command is in a trial recipe, assume that the command has the appropriate permissions
  if (trialPermissionLevel) {
    return trialPermissionLevel;
  }

  //if the command is not in a trial recipe, check if the client has the appropriate permissions
  if (!commandPermissionLevel) {
    return true;
  }
  if (commandPermissionLevel === 'addon' || commandPermissionLevel === 'unlisted') {
    if (clientAppPermissions?.permissions?.find((x) => x.name === commandName)) {
      return true;
    }
  } else {
    if (hasCommandTierPermission(commandPermissionLevel, subscriptionCode)) {
      return true;
    } else if (clientAppPermissions?.permissions?.find((x) => x.name === commandName)) {
      return true;
    }
  }
  return false;
}

export function getPermissionLevelPriority(permissionLevel: AppItemPermissionLevel): number {
  switch (permissionLevel) {
    case AppItemPermissionLevel.Team:
      return 0;
    case AppItemPermissionLevel.Advanced:
      return 1;
    case AppItemPermissionLevel.Enterprise:
      return 2;
    case AppItemPermissionLevel.AddOn:
      return 3;
    case AppItemPermissionLevel.Unlisted:
      return 4;
    case AppItemPermissionLevel.Deprecated:
      return 5;
  }
}

export function getAppPermissionLevel(app: PublishedApp): AppItemPermissionLevel {
  let lowestPermission;
  for (const command of app.package.commands) {
    const permissionLevel = app.permissions?.find((permission) => permission.name === command.key)?.level || AppItemPermissionLevel.Unlisted;
    if (lowestPermission === undefined) {
      lowestPermission = permissionLevel;
    } else if (getPermissionLevelPriority(permissionLevel) < getPermissionLevelPriority(lowestPermission)) {
      lowestPermission = permissionLevel;
    }
  }
  return lowestPermission;
}

export function getTriggerName(trigger: Trigger): string {
  switch (trigger.type) {
    case 'appwebhook':
      return trigger.webhookId;
    case 'appevent':
      return trigger.eventType;
    default:
      return trigger.id;
  }
}

export function getCommandSortPriority(
  command: Action,
  clientAppPermissions: ClientAppPermissions,
  accountSubscription: string,
  appId: string,
  trialRecipes: Recipe[]
): number {
  if (hasCommandPermission(command.appCommand, command.permissionLevel, clientAppPermissions, accountSubscription, appId, trialRecipes)) {
    return -1;
  }

  return getPermissionLevelPriority(command.permissionLevel);
}

export function getTriggerPermissionName(trigger: WorkflowTrigger): string {
  switch (trigger.type) {
    case 'appwebhook':
      return trigger.entityId;
    case 'appevent':
      return trigger.eventType;
    default:
      return null;
  }
}

export function checkTriggerPermission(trigger: WorkflowTrigger, clientAppPermissions: ClientAppPermissions[], publishedApps: PublishedApp[]): boolean {
  const hasWorkflowDeactivateFeature = canIUse(Features.WorkflowCeaseOnLostPermission);
  if (!hasWorkflowDeactivateFeature) {
    return true;
  }
  const triggerName = getTriggerPermissionName(trigger);
  // This is to handle legacy triggers that are not affected by clientapp/tier changes.
  if (triggerName === null) return true;

  const app = publishedApps.find((pa) => pa.appId === trigger.appId);
  if (!app) {
    return true;
  }

  const appItem = app?.permissions?.find((p) => (p.type === 2 && p.name === triggerName) || (p.type === 1 && p.name === triggerName));
  const requiredSubscriptionTier = appItem?.level || 'unlisted';
  if (clientHasPermissionToTier(requiredSubscriptionTier)) {
    return true;
  } else {
    const clientAppPermission = clientAppPermissions?.findIndex((c) => c.appId === trigger.appId && c.permissions.some((p) => p.name === triggerName));
    if (clientAppPermission > -1) {
      return true;
    }
  }
  return false;
}

export function checkStepPermission(step: WorkflowStep, clientAppPermissions: ClientAppPermissions[], publishedApps: PublishedApp[]): boolean {
  const hasWorkflowDeactivateFeature = canIUse(Features.WorkflowCeaseOnLostPermission);
  if (!hasWorkflowDeactivateFeature) {
    return true;
  }
  const app = publishedApps.find((pa) => pa.appId === step.config.appId);
  if (!app) {
    return true;
  }

  const appItem = app?.permissions?.find((p) => p.name === step.config.appCommand);

  const requiredSubscriptionTier = appItem?.level || 'unlisted';
  if (clientHasPermissionToTier(requiredSubscriptionTier)) {
    return true;
  } else {
    const clientAppPermission = clientAppPermissions?.findIndex(
      (c) => c.appId === step.config.appId && c.permissions.some((p) => p.name === step.config.appCommand)
    );
    if (clientAppPermission > -1) {
      return true;
    }
  }
  return false;
}

export function getObjectListFromValue(value: string) {
  try {
    return value ? JSON.parse(value) : [];
  } catch (err) {
    return [];
  }
}

export function getInputConfigKeysForRow(inputConfig: InputConfigItem, rowIndex: number) {
  const keys = [];
  if (inputConfig) {
    for (const inputConfigKey of Object.keys(inputConfig)) {
      const indexMatch = inputConfigKey.match(/._(\d+)_./);
      if (indexMatch && indexMatch.length > 1) {
        const inputConfigIndex = Number(indexMatch[1]);
        if (inputConfigIndex === rowIndex) {
          keys.push(inputConfigKey);
        }
      }
    }
  }
  return keys;
}
